// src/pages/Apps.jsx
// Resources → Apps (3.82.1, owner, Nov 4): the apps to download. A card says
// Coming soon until it has `files` (our private bucket, lib/appDownloads.js)
// or a `url` (a store page). The Web App is the installable portal.
import { PAGE_TITLE, SECTION_SUB } from '../theme/tabs';
import { useState } from 'react';
import { usePhone } from '../hooks/useWindowWidth';
import useInstallApp from '../hooks/useInstallApp';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from '../components/Notifications';
import { API_BASE } from '../firebase/config';
import InstallGuide from '../components/InstallGuide';

const ICONS = {
  android: <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24a11.43 11.43 0 0 0-8.94 0L5.65 5.67c-.19-.29-.58-.38-.87-.2-.28.18-.37.54-.22.83L6.4 9.48A10.81 10.81 0 0 0 1 18h22a10.81 10.81 0 0 0-5.4-8.52zM7 15.25a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zm10 0a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z" />,
  apple: <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />,
  windows: <path d="M3 5.5l7.5-1v7H3v-6zm8.5-1.2L21 3v8.5h-9.5V4.3zM3 12.5h7.5v7L3 18.5v-6zm8.5 0H21V21l-9.5-1.3v-7.2z" />,
  linux: <>
    <path d="M12 2c-2.6 0-4.3 2-4.3 4.7 0 1.5-.3 2.5-1.1 3.8C5.3 12.6 4.5 14.6 4.5 16.6c0 .8.1 1.4.3 2l-1.2.9c-.5.4-.4 1.1.2 1.3l2.9 1c.5.2 1 .1 1.4-.2l.6-.5c1 .4 2.1.6 3.3.6s2.3-.2 3.3-.6l.6.5c.4.3.9.4 1.4.2l2.9-1c.6-.2.7-.9.2-1.3l-1.2-.9c.2-.6.3-1.2.3-2 0-2-.8-4-2.1-6.1-.8-1.3-1.1-2.3-1.1-3.8C16.3 4 14.6 2 12 2z" />
    <path fill="var(--c-f5f5f5)" d="M12 9.6c-2.4 0-4.3 3-4.3 6.3 0 2.6 1.9 3.9 4.3 3.9s4.3-1.3 4.3-3.9c0-3.3-1.9-6.3-4.3-6.3z" />
    <circle cx="10.4" cy="6.4" r="1" fill="var(--c-f5f5f5)" /><circle cx="13.6" cy="6.4" r="1" fill="var(--c-f5f5f5)" />
    <path fill="var(--c-f5f5f5)" d="M12 7.6l1.7 1.1-1.7 1-1.7-1z" />
  </>,
  web: <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95c-.32-1.25-.78-2.45-1.38-3.56 1.84.63 3.37 1.91 4.33 3.56zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2s.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56-1.84-.63-3.37-1.9-4.33-3.56zm2.95-8H5.08c.96-1.66 2.49-2.93 4.33-3.56C8.81 5.55 8.35 6.75 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2s.07-1.35.16-2h4.68c.09.65.16 1.32.16 2s-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95c-.96 1.65-2.49 2.93-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2s-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z" />,
};

const SECTIONS = [
  { title: 'Desktop Apps', apps: [
    { icon: 'windows', name: 'Windows App', about: 'Windows computer', url: null },
    { icon: 'apple', name: 'Mac App', about: 'Mac computer', url: null },
    { icon: 'linux', name: 'Linux App', about: 'For Debian, Ubuntu and Raspberry Pi OS',
      files: [['linux-x86', 'Download for Intel or AMD (x86)'], ['linux-arm', 'Download for ARM (Raspberry Pi and others)']] },
  ] },
  { title: 'Phone Apps', apps: [
    { icon: 'android', name: 'Android App', about: 'Phone or tablet', url: null },
    { icon: 'apple', name: 'iOS App', about: 'iPhone and iPad', url: null },
  ] },
  { title: 'Web Apps', apps: [
    { icon: 'web', name: 'Web App', about: 'Install from your browser.', web: true },
  ] },
];

export default function Apps() {
  const phone = usePhone();
  const { installed, canInstall, install } = useInstallApp();
  const [guide, setGuide] = useState(false);   // iPhone and iPad: the steps
  const { getToken } = useAuth();
  const { notify } = useNotify();
  const [busy, setBusy] = useState(null);
  // 3.83.2: the file is private; the backend gives a signed-in person a short link
  async function download(id) {
    setBusy(id);
    try {
      const r = await fetch(`${API_BASE}/portal/apps/${id}/download`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Couldn't start the download. Please try again.");
      window.location.assign(j.url);
    } catch (e) { notify({ tone: 'error', title: 'Couldn’t start the download', detail: e.message }); }
    setBusy(null);
  }
  // The installable portal (3.52.0): works today, nothing to wait for
  const webApp = installed ? <span style={S.soon}>You're using the installed app</span>
    : canInstall ? <button onClick={async () => { if (await install() === 'ios') setGuide(true); }} style={S.download}>Install</button>
    : <span style={S.note}>No Install button? It's already installed, or try Chrome or Edge.</span>;
  return (
    <div style={S.page}>
      <h2 style={PAGE_TITLE}>Apps</h2>
      <p style={SECTION_SUB}>Download links will appear here as each app becomes ready. The Web App is ready now.</p>
      {/* Three columns: Desktop across the top, Phone and Web under it, so the cards line up */}
      <div style={{ ...S.frames, gridTemplateColumns: phone ? '1fr' : 'repeat(3, minmax(0, 1fr))' }}>
        {SECTIONS.map(s => (
          <section key={s.title} style={{ ...S.frame, ...(phone ? null : { gridColumn: `span ${s.apps.length}` }) }}>
            <h3 style={S.heading}>{s.title}</h3>
            <div style={{ ...S.grid, gap: phone ? 10 : CARD_GAP, gridTemplateColumns: `repeat(${phone ? Math.min(s.apps.length, 2) : s.apps.length}, minmax(0, 1fr))` }}>
              {s.apps.map(a => (
                <div key={a.name} style={S.card}>
                  <svg viewBox="0 0 24 24" width="38" height="38" fill="var(--t-263238)" aria-hidden="true">{ICONS[a.icon]}</svg>
                  <div style={S.name}>{a.name}</div>
                  <div style={S.about}>{a.about}</div>
                  {a.web ? webApp : a.files ? <div style={S.files}>{a.files.map(([id, label]) => (
                    <button key={id} onClick={() => download(id)} disabled={busy === id} style={S.download}>{busy === id ? 'Starting…' : label}</button>
                  ))}</div> : a.url
                    ? <a href={a.url} style={S.download}>Download</a>
                    : <span style={S.soon}>Coming soon</span>}
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
      {guide && <InstallGuide onClose={() => setGuide(false)} />}
    </div>
  );
}

// Cards line up from one row of frames to the next when the gap between them
// is the gap between frames plus both frames' side padding
const FRAME_GAP = 12, FRAME_PAD = 12, CARD_GAP = FRAME_GAP + 2 * FRAME_PAD;

const S = {
  frames: { display: 'grid', gap: FRAME_GAP, marginTop: 14 },
  frame: { background: 'var(--c-ffffff)', border: '1px solid var(--b-c8e6c9)', borderRadius: 12, boxShadow: '0 1px 4px rgb(var(--c-000000-rgb) / 0.06)', padding: `12px ${FRAME_PAD}px`, minWidth: 0, boxSizing: 'border-box' },
  heading: { fontSize: 15, fontWeight: 700, color: 'var(--t-00695c)', margin: '0 0 10px' },
  grid: { display: 'grid' },
  card: { background: 'var(--c-f5f5f5)', borderRadius: 10, padding: '14px 10px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 0 },
  name: { fontSize: 14, fontWeight: 700, color: 'var(--t-263238)', marginTop: 4 },
  about: { fontSize: 12, color: 'var(--t-546e7a)', lineHeight: 1.4 },
  download: { marginTop: 'auto', padding: '6px 14px', background: 'var(--c-00897b)', color: 'var(--c-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', textDecoration: 'none' },
  soon: { marginTop: 'auto', padding: '4px 12px', background: 'var(--c-eceff1)', color: 'var(--t-546e7a)', borderRadius: 999, fontSize: 11, fontWeight: 600 },
  files: { marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 6, width: '100%', paddingTop: 6 },
  note: { marginTop: 'auto', fontSize: 11, color: 'var(--t-78909c)', lineHeight: 1.4 },
};
